import React, { useState } from 'react';
import styled from 'styled-components';
import { useArtists } from '../../contexts/ArtistsContext';
import { approveArtistRegistration, rejectArtistRegistration } from '../../services/artistService';

const PageContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: white;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
`;

const ToggleInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

const ToggleSwitch = styled.span`
  position: relative;
  width: 42px;
  height: 24px;
  flex: 0 0 auto;
  border-radius: 999px;
  background: ${({ $checked }) => ($checked ? 'var(--color-accent)' : 'var(--color-border)')};
  transition: background var(--transition-speed) ease;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${({ $checked }) => ($checked ? '21px' : '3px')};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: white;
    box-shadow: 0 2px 8px rgba(33, 26, 18, 0.18);
    transition: left var(--transition-speed) ease;
  }
`;

const ToggleText = styled.span`
  line-height: 1.2;
`;

const Title = styled.h1`
  font-size: var(--font-size-3xl);
  font-family: var(--font-serif);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
`;

const PlaceholderCard = styled.div`
  padding: var(--space-xl);
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: var(--space-md);
  min-height: 400px;
`;

const PlaceholderTitle = styled.h2`
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
  margin: 0;
`;

const RefreshButton = styled.button`
  background: white;
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  padding: var(--space-sm) var(--space-lg);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-primary-lighter);
    border-color: var(--color-accent);
    color: var(--color-primary);
  }
`;

const RequestList = styled.div`
  display: grid;
  gap: var(--space-md);
`;

const RequestCard = styled.article`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-md);
  padding: var(--space-lg);
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const RequestEmail = styled.a`
  color: var(--color-primary);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  text-decoration: none;

  &:hover {
    color: var(--color-accent);
  }
`;

const RequestMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs);
  margin-top: var(--space-sm);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const PortfolioLink = styled.a`
  display: inline-flex;
  margin-top: var(--space-sm);
  color: var(--color-accent);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  text-decoration: none;
  overflow-wrap: anywhere;

  &:hover {
    color: var(--color-primary);
    text-decoration: underline;
  }
`;

const PortfolioText = styled.div`
  margin-top: var(--space-sm);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  overflow-wrap: anywhere;
`;

const StatusBadge = styled.span`
  align-self: flex-start;
  justify-self: end;
  padding: 6px 10px;
  border-radius: var(--border-radius-sm);
  background: rgba(130, 172, 216, 0.14);
  color: var(--color-accent);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media (max-width: 640px) {
    justify-self: start;
  }
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--space-xs);
  margin-top: var(--space-md);

  @media (max-width: 640px) {
    justify-content: flex-start;
  }
`;

const ActionButton = styled.button`
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-sm);
  padding: 8px 12px;
  background: ${({ $variant }) => ($variant === 'primary' ? 'var(--color-accent)' : 'white')};
  color: ${({ $variant }) => ($variant === 'primary' ? 'white' : 'var(--color-text-secondary)')};
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--color-accent);
    color: ${({ $variant }) => ($variant === 'primary' ? 'white' : 'var(--color-primary)')};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.56;
  }
`;

const ErrorCard = styled.div`
  padding: var(--space-lg);
  background: rgba(180, 35, 24, 0.08);
  border: 1px solid rgba(180, 35, 24, 0.22);
  border-radius: var(--border-radius-md);
  color: var(--color-error);
  font-weight: var(--font-weight-medium);
`;

const formatDate = (date) => {
  if (!date) {
    return 'Unknown date';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const hasUrlScheme = (value) => /^[a-z][a-z\d+.-]*:\/\//i.test(value);

const getPortfolioUrl = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return null;
  }

  const candidate = hasUrlScheme(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(candidate);
    if (!['http:', 'https:'].includes(url.protocol)) {
      return null;
    }
    return url;
  } catch {
    return null;
  }
};

const formatPortfolioLabel = (portfolioUrl) => {
  const parsedUrl = getPortfolioUrl(portfolioUrl);
  if (!parsedUrl) {
    return String(portfolioUrl || '').trim();
  }

  const label = `${parsedUrl.hostname}${parsedUrl.pathname}${parsedUrl.search}`;
  return label.endsWith('/') ? label.slice(0, -1) : label;
};

const PendingRequests = () => {
  const { registrations, loadingRegistrations, registrationError } = useArtists();
  const [processingAction, setProcessingAction] = useState(null);
  const [actionError, setActionError] = useState('');
  const [showProcessedRequests, setShowProcessedRequests] = useState(false);

  const visibleRegistrations = showProcessedRequests
    ? registrations
    : registrations.filter((registration) => registration.status === 'pending');

  const handleRefresh = () => {
    window.location.reload();
  };

  const runRegistrationAction = async (registrationId, action) => {
    const actionKey = `${registrationId}:${action}`;
    setProcessingAction(actionKey);
    setActionError('');

    try {
      if (action === 'approve') {
        await approveArtistRegistration(registrationId);
      } else {
        await rejectArtistRegistration(registrationId);
      }
    } catch (error) {
      console.error(`Failed to ${action} artist registration`, error);
      const fallbackMessage =
        action === 'approve'
          ? 'Unable to approve and email the artist. Please try again.'
          : 'Unable to reject the artist request. Please try again.';
      setActionError(
        error?.name === 'ApiError' && error.message
          ? error.message
          : fallbackMessage
      );
    } finally {
      setProcessingAction(null);
    }
  };

  return (
    <PageContainer>
      <Header>
        <Title>Pending Requests</Title>
        <Subtitle>Manage artists who have applied to join the platform.</Subtitle>
      </Header>

      <ToggleRow>
        <ToggleLabel>
          <ToggleInput
            type="checkbox"
            checked={showProcessedRequests}
            onChange={(event) => setShowProcessedRequests(event.target.checked)}
          />
          <ToggleSwitch $checked={showProcessedRequests} aria-hidden="true" />
          <ToggleText>Show approved/rejected requests</ToggleText>
        </ToggleLabel>
      </ToggleRow>

      {actionError && (
        <ErrorCard role="alert">{actionError}</ErrorCard>
      )}

      {registrationError ? (
        <ErrorCard role="alert">{registrationError}</ErrorCard>
      ) : loadingRegistrations ? (
        <PlaceholderCard>
          <PlaceholderTitle>Loading requests...</PlaceholderTitle>
        </PlaceholderCard>
      ) : visibleRegistrations.length === 0 ? (
        <PlaceholderCard>
          <PlaceholderTitle>No requests found. Awaiting requests.</PlaceholderTitle>
          <RefreshButton onClick={handleRefresh}>
            Refresh
          </RefreshButton>
        </PlaceholderCard>
      ) : (
        <RequestList>
          {visibleRegistrations.map((registration) => {
            const portfolioUrl = getPortfolioUrl(registration.portfolioUrl);
            const portfolioLabel = formatPortfolioLabel(registration.portfolioUrl);

            return (
              <RequestCard key={registration.id}>
                <div>
                  <RequestEmail href={`mailto:${registration.email}`}>
                    {registration.email}
                  </RequestEmail>
                  <RequestMeta>
                    <span>Submitted {formatDate(registration.requestedAt)}</span>
                    {[registration.city, registration.country].filter(Boolean).join(', ') && (
                      <span>{[registration.city, registration.country].filter(Boolean).join(', ')}</span>
                    )}
                    {registration.registeredBusiness && (
                      <span>Registered company / sole trader</span>
                    )}
                    <span>UID {registration.uid}</span>
                  </RequestMeta>
                  {registration.portfolioUrl && (
                    portfolioUrl ? (
                      <PortfolioLink
                        href={portfolioUrl.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Portfolio / social: {portfolioLabel}
                      </PortfolioLink>
                    ) : (
                      <PortfolioText>
                        Portfolio / social: {portfolioLabel}
                      </PortfolioText>
                    )
                  )}
                </div>
                <div>
                  <StatusBadge>{registration.status}</StatusBadge>
                  {registration.status === 'pending' && (
                    <ActionRow>
                      <ActionButton
                        type="button"
                        $variant="primary"
                        disabled={Boolean(processingAction)}
                        onClick={() => runRegistrationAction(registration.id, 'approve')}
                      >
                        {processingAction === `${registration.id}:approve` ? 'Approving...' : 'Approve & Email'}
                      </ActionButton>
                      <ActionButton
                        type="button"
                        disabled={Boolean(processingAction)}
                        onClick={() => runRegistrationAction(registration.id, 'reject')}
                      >
                        {processingAction === `${registration.id}:reject` ? 'Rejecting...' : 'Reject'}
                      </ActionButton>
                    </ActionRow>
                  )}
                </div>
              </RequestCard>
            );
          })}
        </RequestList>
      )}
    </PageContainer>
  );
};

export default PendingRequests;
